import React, { useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { User, Building2, ChevronDown, Shield, Lock, Database, Server, ShieldCheck, Eye, EyeOff, Tag, Share2, Copy, Mail, MessageCircle, X } from 'lucide-react';
import './LandingPage.css';

const LandingPage = ({ setIsAuthenticated, setIsAdmin }) => {
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [accountType, setAccountType] = useState('individual'); // individual or company
  const [billingPlan, setBillingPlan] = useState('monthly'); // monthly or yearly for individual
  const [companySize, setCompanySize] = useState('small'); // small (1-50) or large (51-500) for company
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeValid, setPromoCodeValid] = useState(null); // null, true, or false
  const [promoValidating, setPromoValidating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // New: Promo code registration flow
  const [hasPromoCode, setHasPromoCode] = useState(false);
  const [promoCodeVerified, setPromoCodeVerified] = useState(false);
  const [promoCodeDetails, setPromoCodeDetails] = useState(null);
  // Share modal
  const [showShareModal, setShowShareModal] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    company_name: '',
    sms_opt_in: false, // SMS notifications opt-in
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('username', formData.email);
      formBody.append('password', formData.password);

      const response = await axiosInstance.post('/auth/login', formBody, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      localStorage.setItem('token', response.data.access_token);
      
      // Get user info to check admin status
      const userResponse = await axiosInstance.get('/auth/me', {
        headers: { Authorization: `Bearer ${response.data.access_token}` }
      });
      
      setIsAdmin(userResponse.data.is_admin || false);
      setIsAuthenticated(true);
      toast.success('Login successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const validatePromoCode = async (code) => {
    if (!code.trim()) {
      setPromoCodeValid(null);
      return;
    }
    
    setPromoValidating(true);
    try {
      const response = await axiosInstance.post('/promo-codes/validate', {
        code: code.trim(),
        account_type: accountType
      });
      setPromoCodeValid(true);
      setPromoCodeDetails(response.data);
      setPromoCodeVerified(true);
      toast.success('Promo code verified! Complete your registration below.');
    } catch (error) {
      setPromoCodeValid(false);
      setPromoCodeVerified(false);
      setPromoCodeDetails(null);
      toast.error(error.response?.data?.detail || 'Invalid promo code');
    } finally {
      setPromoValidating(false);
    }
  };

  // Validate promo code for the promo flow (before showing form)
  const handleVerifyPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    await validatePromoCode(promoCode);
  };

  // Reset promo code flow
  const resetPromoCodeFlow = () => {
    setHasPromoCode(false);
    setPromoCodeVerified(false);
    setPromoCode('');
    setPromoCodeValid(null);
    setPromoCodeDetails(null);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate company name for company accounts
    if (accountType === 'company' && !formData.company_name.trim()) {
      toast.error('Company name is required for company accounts');
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post('/auth/register', {
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        phone: formData.phone,
        account_type: accountType,
        company_name: accountType === 'company' ? formData.company_name : null,
        promo_code: promoCode.trim() || null,
        sms_opt_in: formData.sms_opt_in, // SMS consent
      });

      localStorage.setItem('token', response.data.access_token);
      
      // Get user info to check admin status
      const userResponse = await axiosInstance.get('/auth/me', {
        headers: { Authorization: `Bearer ${response.data.access_token}` }
      });
      
      setIsAdmin(userResponse.data.is_admin || false);
      setIsAuthenticated(true);
      toast.success(promoCode.trim() ? 'Account created with promo code!' : 'Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axiosInstance.post('/auth/forgot-password', {
        email: resetEmail,
      });

      toast.success('Password reset link has been sent to your email!');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landing-page">
      <div className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <img 
            src="/stc-logo.png" 
            alt="Southwest Training Center" 
            className="hero-logo"
          />
          <h1 className="hero-title" data-testid="hero-title">
            Southwest Training Center
            <br />
            Permit Tracker
          </h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            Complete tracker for BSIS, CCW and other permits. Never miss a deadline 
            with automated reminders for all your certifications.
          </p>
          <div className="hero-buttons">
            <button
              className="btn-primary hero-btn"
              onClick={() => setShowAuth(true)}
              data-testid="get-started-btn"
            >
              Get Started
            </button>
            <button
              className="btn-share hero-btn"
              onClick={() => setShowShareModal(true)}
              data-testid="share-btn"
            >
              <Share2 size={18} />
              Share This App
            </button>
          </div>

          {/* Security Trust Badges */}
          <div className="hero-security-badges">
            <div className="hero-trust-badge">
              <Lock size={16} />
              <span>256-bit SSL Encryption</span>
            </div>
            <div className="hero-trust-badge">
              <ShieldCheck size={16} />
              <span>Data Protected</span>
            </div>
            <div className="hero-trust-badge">
              <Database size={16} />
              <span>Encrypted Storage</span>
            </div>
            <div className="hero-trust-badge">
              <Server size={16} />
              <span>Secure Cloud</span>
            </div>
          </div>

          <p className="hero-security-text">
            <Shield size={14} />
            Your permit data is protected by enterprise-grade security
          </p>
        </div>
      </div>

      {showAuth && (
        <div className="auth-modal" data-testid="auth-modal">
          <div className="auth-modal-overlay" onClick={() => setShowAuth(false)}></div>
          <div className="auth-modal-content">
            <button
              className="auth-modal-close"
              onClick={() => setShowAuth(false)}
              data-testid="close-modal-btn"
            >
              ×
            </button>

            <div className="auth-header">
              <h2 data-testid="auth-title">{isLogin ? 'Login' : 'Create Account'}</h2>
              <p data-testid="auth-subtitle">
                {isLogin
                  ? 'Enter your credentials to access your dashboard'
                  : 'Register to start tracking your permits'}
              </p>
            </div>

            <div className="auth-tabs">
              <button
                className={`auth-tab ${isLogin ? 'active' : ''}`}
                onClick={() => { setIsLogin(true); resetPromoCodeFlow(); }}
                data-testid="login-tab"
              >
                Login
              </button>
              <button
                className={`auth-tab ${!isLogin ? 'active' : ''}`}
                onClick={() => { setIsLogin(false); resetPromoCodeFlow(); }}
                data-testid="register-tab"
              >
                Register
              </button>
            </div>

            {/* LOGIN FORM */}
            {isLogin && (
              <form onSubmit={handleLogin} className="auth-form" data-testid="auth-form">
                <div className="form-group">
                  <label className="label">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="input-field"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    data-testid="email-input"
                  />
                </div>

                <div className="form-group">
                  <label className="label">Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      className="input-field"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-primary auth-submit-btn"
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? 'Processing...' : 'Login'}
                </button>

                <p className="auth-link">
                  <span onClick={() => setShowForgotPassword(true)}>Forgot Password?</span>
                </p>
              </form>
            )}

            {/* REGISTRATION FORM */}
            {!isLogin && (
              <>
                {/* STEP 1: Choose promo code or regular registration */}
                {!hasPromoCode && !promoCodeVerified && (
                  <div className="registration-choice">
                    {/* Promo Code Option - Prominent */}
                    <div className="promo-code-entry-section">
                      <div className="promo-code-header">
                        <Tag size={20} />
                        <span>Have a Promo Code?</span>
                      </div>
                      <p className="promo-code-description">Enter your code for free access</p>
                      <div className="promo-code-input-group">
                        <input
                          type="text"
                          className={`input-field promo-input ${promoCodeValid === true ? 'valid' : promoCodeValid === false ? 'invalid' : ''}`}
                          value={promoCode}
                          onChange={(e) => {
                            setPromoCode(e.target.value.toUpperCase());
                            setPromoCodeValid(null);
                          }}
                          placeholder="ENTER PROMO CODE"
                          data-testid="promo-code-input"
                        />
                        <button
                          type="button"
                          className="btn-validate-promo"
                          onClick={handleVerifyPromoCode}
                          disabled={!promoCode.trim() || promoValidating}
                        >
                          {promoValidating ? '...' : 'Apply'}
                        </button>
                      </div>
                      {promoCodeValid === false && (
                        <p className="promo-error">✗ Invalid or expired promo code</p>
                      )}
                    </div>

                    <div className="registration-divider">
                      <span>OR</span>
                    </div>

                    {/* Regular Registration Option */}
                    <button
                      type="button"
                      className="btn-secondary start-regular-registration"
                      onClick={() => setHasPromoCode(false)}
                    >
                      Continue with Paid Plan
                    </button>
                    <p className="trial-hint">14-day free trial included</p>

                    {/* Account Type Selection for Regular Registration */}
                    <div className="form-group" style={{ marginTop: '20px' }}>
                      <label className="label">Account Type</label>
                      <div className="account-type-selector">
                        <div
                          className={`account-type-option ${accountType === 'individual' ? 'selected' : ''}`}
                          onClick={() => setAccountType('individual')}
                          data-testid="individual-option"
                        >
                          <User size={24} />
                          <div className="option-details">
                            <span className="option-title">Individual</span>
                            <span className="option-price">
                              {billingPlan === 'monthly' ? '$2.99/month' : '$29.99/year'}
                            </span>
                          </div>
                        </div>
                        <div
                          className={`account-type-option ${accountType === 'company' ? 'selected' : ''}`}
                          onClick={() => setAccountType('company')}
                          data-testid="company-option"
                        >
                          <Building2 size={24} />
                          <div className="option-details">
                            <span className="option-title">Company</span>
                            <span className="option-price">
                              {companySize === 'small' ? '$129.99/year' : '$229.99/year'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Billing Plan Dropdown for Individual */}
                      {accountType === 'individual' && (
                        <>
                          <div className="billing-plan-selector">
                            <label className="label">Billing Plan</label>
                            <div className="select-wrapper">
                              <select
                                value={billingPlan}
                                onChange={(e) => setBillingPlan(e.target.value)}
                                className="input-field billing-select"
                                data-testid="billing-plan-select"
                              >
                                <option value="monthly">Monthly - $2.99/month</option>
                                <option value="yearly">Yearly - $29.99/year (Save $6!)</option>
                              </select>
                              <ChevronDown className="select-icon" size={18} />
                            </div>
                          </div>
                          <div className="unlimited-permits-note">
                            <span>Once you sign up you can create unlimited number of permits to track.</span>
                          </div>
                        </>
                      )}

                      {/* Company Size Dropdown for Company */}
                      {accountType === 'company' && (
                        <div className="billing-plan-selector">
                          <label className="label">Company Size</label>
                          <div className="select-wrapper">
                            <select
                              value={companySize}
                              onChange={(e) => setCompanySize(e.target.value)}
                              className="input-field billing-select"
                              data-testid="company-size-select"
                            >
                              <option value="small">1-50 Employees - $129.99/year</option>
                              <option value="large">51-500 Employees - $229.99/year</option>
                            </select>
                            <ChevronDown className="select-icon" size={18} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* STEP 2: Registration Form (shown after promo verified OR for regular registration) */}
                {(promoCodeVerified || (!hasPromoCode && accountType)) && (
                  <form onSubmit={handleRegister} className="auth-form" data-testid="auth-form">
                    {/* Promo Code Badge */}
                    {promoCodeVerified && (
                      <div className="promo-verified-badge">
                        <Tag size={16} />
                        <span>Promo Code: <strong>{promoCode}</strong></span>
                        <button type="button" className="change-promo-btn" onClick={resetPromoCodeFlow}>
                          Change
                        </button>
                      </div>
                    )}

                    {/* Account Type Selection (only for promo code flow) */}
                    {promoCodeVerified && (
                      <div className="form-group">
                        <label className="label">Account Type</label>
                        <div className="account-type-selector">
                          <div
                            className={`account-type-option ${accountType === 'individual' ? 'selected' : ''}`}
                            onClick={() => setAccountType('individual')}
                          >
                            <User size={24} />
                            <div className="option-details">
                              <span className="option-title">Individual</span>
                              <span className="option-price promo-price">FREE with promo</span>
                            </div>
                          </div>
                          <div
                            className={`account-type-option ${accountType === 'company' ? 'selected' : ''}`}
                            onClick={() => setAccountType('company')}
                          >
                            <Building2 size={24} />
                            <div className="option-details">
                              <span className="option-title">Company</span>
                              <span className="option-price promo-price">FREE with promo</span>
                            </div>
                          </div>
                        </div>
                        {accountType === 'individual' && (
                          <div className="unlimited-permits-note">
                            <span>Once you sign up you can create unlimited number of permits to track.</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Company Name - First for Company Accounts */}
                    {accountType === 'company' && (
                      <div className="form-group">
                        <label className="label">Company Name</label>
                        <input
                          type="text"
                          name="company_name"
                          className="input-field"
                          placeholder="Your company name"
                          value={formData.company_name}
                          onChange={handleInputChange}
                          required
                          data-testid="company-name-input"
                        />
                      </div>
                    )}

                    {/* Full Name / Admin Name */}
                    <div className="form-group">
                      <label className="label">{accountType === 'company' ? 'Admin Name' : 'Full Name'}</label>
                      <input
                        type="text"
                        name="full_name"
                        className="input-field"
                        placeholder={accountType === 'company' ? 'Company administrator name' : 'Your full name'}
                        value={formData.full_name}
                        onChange={handleInputChange}
                        required
                        data-testid="full-name-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="label">{accountType === 'company' ? 'Admin Email' : 'Email'}</label>
                      <input
                        type="email"
                        name="email"
                        className="input-field"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        data-testid="email-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="label">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        className="input-field"
                        placeholder="+1 (555) 123-4567"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        data-testid="phone-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="label">Password</label>
                      <div className="password-input-wrapper">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          className="input-field"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                          minLength={6}
                          data-testid="password-input"
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <p className="field-hint">Minimum 6 characters</p>
                    </div>

                    {/* Notification Preferences */}
                    <div className="form-group notification-preferences">
                      <label className="label">Notification Preferences</label>
                      
                      <div className="checkbox-option disabled-option">
                        <input
                          type="checkbox"
                          checked={true}
                          disabled
                          readOnly
                        />
                        <div className="checkbox-content">
                          <span className="checkbox-label">Email Reminders</span>
                          <span className="checkbox-required">(Required)</span>
                        </div>
                      </div>
                      <p className="preference-hint">You will receive email reminders for permit expirations and qualifications.</p>
                      
                      <div className="checkbox-option sms-consent">
                        <input
                          type="checkbox"
                          name="sms_opt_in"
                          checked={formData.sms_opt_in}
                          onChange={handleInputChange}
                          data-testid="sms-opt-in"
                        />
                        <div className="checkbox-content">
                          <span className="checkbox-label">SMS Text Reminders</span>
                          <span className="checkbox-optional">(Optional)</span>
                        </div>
                      </div>
                      <p className="preference-hint">
                        By checking this box, I consent to receive SMS text message reminders about my permits from Southwest Training Center. 
                        Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe. 
                        <a href="/sms-consent" target="_blank" rel="noopener noreferrer"> View SMS Terms</a>
                      </p>
                    </div>

                    <button
                      type="submit"
                      className="btn-primary auth-submit-btn"
                      disabled={loading}
                      data-testid="submit-btn"
                    >
                      {loading ? 'Processing...' : 'Create Account'}
                    </button>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {showForgotPassword && (
        <div className="auth-modal" data-testid="forgot-password-modal">
          <div className="auth-modal-overlay" onClick={() => setShowForgotPassword(false)}></div>
          <div className="auth-modal-content">
            <button
              className="auth-modal-close"
              onClick={() => setShowForgotPassword(false)}
              data-testid="close-forgot-modal"
            >
              ×
            </button>

            <div className="auth-header">
              <h2 data-testid="forgot-password-title">Forgot Password?</h2>
              <p data-testid="forgot-password-subtitle">
                Enter your email address and we&apos;ll send you a link to reset your password.
              </p>
            </div>

            <form onSubmit={handleForgotPassword} className="auth-form" data-testid="forgot-password-form">
              <div className="form-group">
                <label className="label">Email Address</label>
                <input
                  type="email"
                  className="input-field"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  placeholder="your.email@example.com"
                  data-testid="reset-email-input"
                />
              </div>

              <button
                type="submit"
                className="btn-primary auth-submit-btn"
                disabled={loading}
                data-testid="send-reset-btn"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>

              <div className="forgot-password-link">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setShowAuth(true);
                  }}
                  data-testid="back-to-login"
                >
                  Back to Login
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="features-section">
        <div className="container">
          <h2 className="section-title" data-testid="features-title">Why Track Your Permits?</h2>
          <div className="features-grid">
            <div className="feature-card" data-testid="feature-reminders">
              <div className="feature-icon">&#128276;</div>
              <h3>Automated Reminders</h3>
              <p>
                Receive email and SMS notifications 30 days before your
                qualification due date.
              </p>
            </div>
            <div className="feature-card" data-testid="feature-tracking">
              <div className="feature-icon">&#128197;</div>
              <h3>Schedule Tracking</h3>
              <p>
                Automatically calculate qualification dates every 5 months within
                your 2-year permit period.
              </p>
            </div>
            <div className="feature-card" data-testid="feature-compliance">
              <div className="feature-icon">&#9989;</div>
              <h3>Stay Compliant</h3>
              <p>
                Never miss a deadline and maintain all your permits
                in good standing.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Security Section */}
      <div className="security-section">
        <div className="container">
          <div className="security-header">
            <div className="security-badge-large">
              <ShieldCheck size={48} />
            </div>
            <h2>Your Data is Protected</h2>
            <p>Industry-leading security measures to keep your permit information safe</p>
          </div>
          
          <div className="security-grid">
            <div className="security-item">
              <div className="security-item-icon ssl">
                <Lock size={28} />
              </div>
              <h4>256-bit SSL Encryption</h4>
              <p>All data encrypted in transit</p>
            </div>
            <div className="security-item">
              <div className="security-item-icon storage">
                <Database size={28} />
              </div>
              <h4>Encrypted Storage</h4>
              <p>Data secured at rest</p>
            </div>
            <div className="security-item">
              <div className="security-item-icon server">
                <Server size={28} />
              </div>
              <h4>Secure Cloud Hosting</h4>
              <p>24/7 monitored infrastructure</p>
            </div>
            <div className="security-item">
              <div className="security-item-icon auth">
                <Shield size={28} />
              </div>
              <h4>Secure Authentication</h4>
              <p>Protected account access</p>
            </div>
          </div>

          <div className="security-certificate-badge">
            <ShieldCheck size={24} />
            <span>Your permit data is protected by enterprise-grade security</span>
          </div>
        </div>
      </div>

      <footer className="landing-footer">
        <div className="container">
          <p>© 2025 Southwest Training Center Permit Tracker. All rights reserved.</p>
          <p>10285 Glenoaks Blvd #6, Pacoima, CA, 91331 | 310-650-6896</p>
        </div>
      </footer>

      {/* Share Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="share-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setShowShareModal(false)}>
              <X size={24} />
            </button>
            <div className="share-modal-header">
              <Share2 size={32} />
              <h2>Share Permit Tracker</h2>
              <p>Help others track their permits and certifications</p>
            </div>
            <div className="share-options">
              <button
                className="share-option-btn email-share"
                onClick={() => {
                  window.open(
                    `mailto:?subject=Check out Southwest Training Center Permit Tracker&body=I wanted to share this useful permit tracking tool with you!%0D%0A%0D%0AIt helps track BSIS, CCW and other permits with automated reminders.%0D%0A%0D%0ACheck it out: ${window.location.href}`,
                    '_blank'
                  );
                }}
              >
                <Mail size={24} />
                <span>Share via Email</span>
              </button>
              <button
                className="share-option-btn sms-share"
                onClick={() => {
                  window.open(
                    `sms:?body=Check out this permit tracking tool: ${window.location.href}`,
                    '_blank'
                  );
                }}
              >
                <MessageCircle size={24} />
                <span>Share via Text</span>
              </button>
              <button
                className="share-option-btn copy-share"
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  toast.success('Link copied to clipboard!');
                }}
              >
                <Copy size={24} />
                <span>Copy Link</span>
              </button>
            </div>
            <div className="share-link-box">
              <input
                type="text"
                value={window.location.href}
                readOnly
                className="share-link-input"
              />
              <button
                className="copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  toast.success('Link copied!');
                }}
              >
                <Copy size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;